import { useState, useMemo } from 'react';

import PropTypes from 'prop-types';
import { BsArrowLeftRight } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import FormField from '#globalComponents/Forms/FormField';
import Select from '#globalComponents/Forms/Select';
import TextInput from '#globalComponents/Forms/TextInput';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';
import { useError } from '#globalContexts/ErrorContext';
import { formatCount } from '#lib/numberFormat';
import { useTransferCredits } from '#orgFeature/models/org/mutations';

const CREDIT_TYPE_OPTIONS = [
	{ value: 'companyCredits', label: 'Firmen-Credits' },
	{ value: 'contactCredits', label: 'Kontakt-Credits' },
];

export const TransferCreditsModal = ({ isOpen, onClose, members, senderId }) => {
	const [receiverId, setReceiverId] = useState('');
	const [creditType, setCreditType] = useState('companyCredits');
	const [amount, setAmount] = useState('');

	const { showSuccess, showError } = useError();
	const transferMutation = useTransferCredits();

	const senderMember = useMemo(() => members.find((m) => m.userId === senderId), [members, senderId]);

	const possibleReceivers = useMemo(() => members.filter((m) => m.userId !== senderId), [members, senderId]);

	const availableCredits =
		creditType === 'companyCredits' ? (senderMember?.companyCredits ?? 0) : (senderMember?.contactCredits ?? 0);

	const handleTransfer = async () => {
		const numAmount = Number(amount);
		if (!receiverId || !numAmount || numAmount <= 0) return;

		try {
			await transferMutation.mutateAsync({ senderId, receiverId, creditType, amount: numAmount });
			handleClose();
			showSuccess({
				title: 'Credits übertragen',
				message: 'Die Credits wurden erfolgreich übertragen.',
			});
		} catch (err) {
			showError({
				title: 'Fehler bei Übertragung',
				error: err,
			});
		}
	};

	const handleClose = () => {
		setAmount('');
		setReceiverId('');
		onClose();
	};

	return (
		<Modal isOpen={isOpen} onClose={handleClose} size="s">
			<Panel.Header icon={BsArrowLeftRight} onClose={handleClose}>
				Credits übertragen
			</Panel.Header>

			<Panel.Body>
				<div className="space-y-4">
					<FormField label="Empfänger" htmlFor="transfer-receiver">
						<Select
							id="transfer-receiver"
							value={receiverId}
							onChange={(e) => setReceiverId(e.target.value)}
						>
							<option value="">Mitglied auswählen...</option>
							{possibleReceivers.map((m) => (
								<option key={m.userId} value={m.userId}>
									{m.fullName || m.email}
								</option>
							))}
						</Select>
					</FormField>

					<FormField label="Credit-Typ" htmlFor="transfer-creditType">
						<Select
							id="transfer-creditType"
							value={creditType}
							onChange={(e) => setCreditType(e.target.value)}
						>
							{CREDIT_TYPE_OPTIONS.map((opt) => (
								<option key={opt.value} value={opt.value}>
									{opt.label}
								</option>
							))}
						</Select>
					</FormField>

					<FormField
						label="Anzahl"
						htmlFor="transfer-amount"
						hint={`Verfügbar: ${formatCount(availableCredits)} ${CREDIT_TYPE_OPTIONS.find((o) => o.value === creditType)?.label}`}
					>
						<TextInput
							id="transfer-amount"
							type="number"
							min="1"
							max={availableCredits}
							value={amount}
							onChange={(e) => setAmount(e.target.value)}
							placeholder="0"
						/>
					</FormField>
				</div>
			</Panel.Body>

			<Panel.Footer stacked>
				<Button
					variant="solid"
					size="md"
					fullWidth
					onClick={handleTransfer}
					disabled={
						transferMutation.isPending ||
						!receiverId ||
						!amount ||
						Number(amount) <= 0 ||
						Number(amount) > availableCredits
					}
					loading={transferMutation.isPending}
				>
					Übertragen
				</Button>
			</Panel.Footer>
		</Modal>
	);
};

TransferCreditsModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	members: PropTypes.arrayOf(
		PropTypes.shape({
			userId: PropTypes.string.isRequired,
			email: PropTypes.string,
			fullName: PropTypes.string,
			companyCredits: PropTypes.number,
			contactCredits: PropTypes.number,
		}),
	).isRequired,
	senderId: PropTypes.string.isRequired,
};
